import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const field = "w-full border border-black/20 bg-white px-4 py-3.5 text-base outline-none transition-colors focus:border-[#FF5A00]";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2" data-testid="admin-login-page">
      <div className="grain relative hidden flex-col justify-between bg-[#0B0B0B] p-12 text-white md:flex">
        <LogoMark className="h-12 w-12" />
        <h2 className="font-wide text-6xl font-black uppercase leading-[0.9] tracking-tight">Manage<br />the <span className="text-[#FFC400]">catalogue.</span></h2>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Tide Inchem · Admin</p>
      </div>
      <div className="flex items-center justify-center px-5 py-16">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
          <LogoMark className="h-10 w-10 md:hidden" />
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-zinc-700 md:mt-0">Admin sign in</p>
          <h1 className="font-wide mt-3 text-3xl font-black uppercase tracking-tight">Welcome back</h1>
          <label className="mt-10 block font-mono text-xs uppercase tracking-[0.15em] text-zinc-700" htmlFor="email">Email</label>
          <input id="email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className={`${field} mt-2`} />
          <label className="mt-6 block font-mono text-xs uppercase tracking-[0.15em] text-zinc-700" htmlFor="password">Password</label>
          <input id="password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className={`${field} mt-2`} />
          {error && <p data-testid="admin-login-error" className="mt-4 border-l-2 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="mt-8 flex w-full items-center justify-center gap-2 bg-[#0B0B0B] px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#FF5A00] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
          <Link to="/admin/forgot-password" data-testid="admin-forgot-link" className="mt-5 inline-block font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 underline-offset-4 hover:text-[#FF5A00] hover:underline">Forgot password?</Link>
        </form>
      </div>
    </div>
  );
}
